import React, { useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs, { toLdItems } from "@/components/Breadcrumbs";
import NotFound from "@/pages/NotFound";
import { SITE_URL, SERVICES, buildBreadcrumbLd, buildFaqLd } from "@/lib/seo";
import data from "@/data/professionnels.data.json";
import { useSeoT, PRO_I18N } from "@/lib/seoPagesI18n";

export default function ProfessionnelsSegment() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, t: st } = useSeoT();
  const [openIdx, setOpenIdx] = useState(-1);
  const base = data.segments.find((s) => s.slug === slug);
  if (!base) return <NotFound />;
  const seg = lang !== "fr" ? { ...base, ...(PRO_I18N[lang]?.segments?.[slug] || {}) } : base;
  const p = st.pro;

  const path = `/professionnels/${seg.slug}`;
  const bc = [
    { name: "Accueil", path: "/" },
    { name: "Professionnels", path: "/professionnels" },
    { name: seg.h1, path },
  ];
  const jsonLd = [
    { "@context": "https://schema.org", "@type": "Service", name: seg.h1, description: seg.metaDescription,
      provider: { "@id": `${SITE_URL}/#organization` }, areaServed: { "@type": "AdministrativeArea", name: "Suisse romande" }, url: `${SITE_URL}${path}` },
    buildBreadcrumbLd(toLdItems(bc)),
  ];
  if (seg.faq && seg.faq.length) jsonLd.push(buildFaqLd(seg.faq));
  const relServices = (seg.services || []).map((s) => SERVICES.find((x) => x.slug === s)).filter(Boolean);
  const whatsappHref = `https://wa.me/41782321850?text=${encodeURIComponent(`Bonjour Mopper, demande professionnelle : ${seg.h1}`)}`;

  return (
    <div className="min-h-screen bg-white text-[#0A0F2C]" data-testid={`professionnels-${seg.slug}`}>
      <Seo title={seg.title} description={seg.metaDescription} path={path} jsonLd={jsonLd} />
      <Header />
      <section className="relative bg-[#2D5BFF] hero-noise overflow-hidden pt-28 md:pt-36 pb-14 md:pb-20 px-5 sm:px-8 md:px-10 lg:px-14 text-white">
        <div className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(ellipse at top right, rgba(255,255,255,0.15), transparent 55%)" }} />
        <div className="relative mx-auto" style={{ maxWidth: "1080px" }}>
          <div className="mb-6"><Breadcrumbs items={bc} variant="dark" /></div>
          <h1 className="font-extrabold text-4xl md:text-5xl lg:text-6xl tracking-tighter leading-[1.02] max-w-3xl" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{seg.h1}</h1>
          <p className="mt-5 text-white/85 text-base md:text-lg max-w-2xl">{seg.intro}</p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3">
            <button onClick={() => navigate("/devis")} className="inline-flex items-center justify-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm md:text-base transition-all" data-testid="pro-seg-cta-quote">
              {p.askQuote} <ArrowRight size={18} />
            </button>
            <a href={whatsappHref} target="_blank" rel="noopener noreferrer nofollow" className="inline-flex items-center justify-center gap-2 rounded-full bg-white/10 hover:bg-white/15 border border-white/20 text-white px-7 py-4 font-semibold text-sm md:text-base transition-all" data-testid="pro-seg-whatsapp">
              <MessageCircle size={18} /> {p.ctaWhatsapp}
            </a>
          </div>
        </div>
      </section>

      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto grid grid-cols-1 lg:grid-cols-2 gap-8" style={{ maxWidth: "1080px" }}>
          <div>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.whatIncludes}</h2>
            <ul className="mt-6 flex flex-col gap-3">
              {seg.benefits.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-[16px]" data-testid={`pro-benefit-${i}`}><span className="mt-1 inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#2D5BFF] text-white shrink-0"><Check size={11} strokeWidth={3} /></span><span>{b}</span></li>
              ))}
            </ul>
          </div>
          <div className="bg-[#F7F7F8] rounded-3xl p-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#2D5BFF]">{p.relatedTitle}</div>
            <div className="mt-4 flex flex-col gap-3">
              {relServices.map((s) => (
                <Link key={s.slug} to={`/services/${s.slug}`} className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid={`pro-rel-service-${s.slug}`}>{s.name} <ArrowRight size={14} /></Link>
              ))}
              <Link to="/tarifs" className="inline-flex items-center gap-2 font-semibold hover:text-[#2D5BFF]" data-testid="pro-rel-tarifs">{p.seeTarifs} <ArrowRight size={14} /></Link>
            </div>
          </div>
        </div>
      </section>

      {/* POURQUOI MOPPER */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
        <div className="mx-auto" style={{ maxWidth: "1080px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.whyTitle}</h2>
          <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
            {p.why.map((w, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-gray-100" data-testid={`pro-why-${i}`}>
                <div className="w-9 h-9 rounded-xl bg-[#EAF0FF] text-[#2D5BFF] inline-flex items-center justify-center"><Check size={16} strokeWidth={3} /></div>
                <h3 className="mt-4 font-extrabold text-lg text-[#0A0F2C]" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{w.t}</h3>
                <p className="mt-2 text-[15px] text-[#4B5563] leading-relaxed">{w.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMMENT ÇA SE PASSE */}
      <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto" style={{ maxWidth: "1080px" }}>
          <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.stepsTitle}</h2>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {p.steps.map((s, i) => (
              <div key={i} className="relative bg-[#0A0F2C] text-white rounded-2xl p-5" data-testid={`pro-step-${i}`}>
                <div className="text-[#FFD84D] font-extrabold text-2xl tabular-nums">{String(i + 1).padStart(2, "0")}</div>
                <p className="mt-2 text-sm text-white/85 leading-relaxed">{s}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BANDE CTA */}
      <section className="py-12 md:py-16 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#2D5BFF] text-white">
        <div className="mx-auto flex flex-col md:flex-row items-center justify-between gap-6" style={{ maxWidth: "1080px" }}>
          <div>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.midCtaTitle}</h2>
            <p className="mt-2 text-white/85">{p.midCtaText}</p>
          </div>
          <button onClick={() => navigate("/devis")} className="shrink-0 inline-flex items-center gap-2 rounded-full bg-[#FFD84D] hover:bg-yellow-300 text-[#0A0F2C] px-7 py-4 font-extrabold text-sm transition-all" data-testid="pro-midcta">
            {p.askQuote} <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {seg.faq && seg.faq.length > 0 && (
        <section className="py-14 md:py-20 px-5 sm:px-8 md:px-10 lg:px-14 bg-[#F7F7F8]">
          <div className="mx-auto" style={{ maxWidth: "900px" }}>
            <h2 className="font-extrabold text-2xl md:text-3xl tracking-tight text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>{p.faqTitle}</h2>
            <div className="mt-8 flex flex-col gap-3">
              {seg.faq.map((it, i) => {
                const open = openIdx === i;
                return (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden" data-testid={`pro-faq-${i}`}>
                    <button className="w-full text-left p-5 md:p-6 flex items-center justify-between gap-4" onClick={() => setOpenIdx(open ? -1 : i)} aria-expanded={open}>
                      <span className="font-bold text-base md:text-lg">{it.q}</span>
                      <ChevronDown className={`shrink-0 text-[#2D5BFF] transition-transform ${open ? "rotate-180" : ""}`} size={20} />
                    </button>
                    {open && <div className="px-5 md:px-6 pb-5 md:pb-6 text-[#4B5563] leading-relaxed">{it.a}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <section className="py-12 px-5 sm:px-8 md:px-10 lg:px-14 bg-white">
        <div className="mx-auto text-center" style={{ maxWidth: "900px" }}>
          <Link to="/professionnels" className="text-sm font-semibold text-[#6B7280] hover:text-[#0A0F2C]">← {p.back}</Link>
        </div>
      </section>
      <Footer />
    </div>
  );
}
